import { useTranslation } from 'react-i18next';
import type { BankAccountAccount } from '@/lib/payments/bankAccountAccount.types';
import { BankAccountRow } from './BankAccountRow';
import { PaymentAccountSection } from './PaymentAccountSection';

interface BankAccountListSectionProps {
  accounts: BankAccountAccount[];
  isLoading?: boolean;
  onAddAccount: () => void;
  onSetDefault: (id: string) => void;
  settingDefaultId?: string;
  onEdit: (id: string) => void;
  onRemove: (account: BankAccountAccount) => void;
  /** Payment_Profile__c ids with a scheduled one-time payment — see selectPaymentProfileIdsWithUpcomingPayment. */
  paymentProfileIdsWithUpcomingPayment?: Set<string>;
}

export function BankAccountListSection({
  accounts,
  isLoading = false,
  onAddAccount,
  onSetDefault,
  settingDefaultId,
  onEdit,
  onRemove,
  paymentProfileIdsWithUpcomingPayment,
}: BankAccountListSectionProps) {
  const { t } = useTranslation('members');

  return (
    <PaymentAccountSection
      heading={t('payments.pages.paymentAccounts.bankAccountsTitle')}
      addLabel={t('payments.pages.paymentAccounts.addBank')}
      onAdd={onAddAccount}
      isLoading={isLoading}
      isEmpty={accounts.length === 0}
      emptyIcon="landmark"
      emptyHeading={t('payments.pages.paymentAccounts.bankAccounts.emptyStateHeading')}
      emptyBody={t('payments.pages.paymentAccounts.bankAccounts.emptyStateBody')}
    >
      {accounts.map((account) => (
        <BankAccountRow
          key={account.id}
          account={account}
          onSetDefault={onSetDefault}
          isSettingDefault={settingDefaultId === account.id}
          onEdit={onEdit}
          onRemove={onRemove}
          hasUpcomingPayment={paymentProfileIdsWithUpcomingPayment?.has(account.id)}
        />
      ))}
    </PaymentAccountSection>
  );
}
